import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Coins, Zap, Check, Gift } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { useProfile } from "../context/ProfileContext";
import { api } from "../lib/api";

export default function Rewards() {
  const { profile, refresh } = useProfile();
  const [quests, setQuests] = useState([]);
  const [claiming, setClaiming] = useState(null);

  const load = () => {
    if (profile) api.getQuests(profile.id).then((q) => setQuests(q.items)).catch(() => {});
  };
  useEffect(load, [profile?.id]); // eslint-disable-line

  const claim = async (qid) => {
    setClaiming(qid);
    try {
      const res = await api.claimQuest(profile.id, qid);
      toast.success(`Claimed +${res.reward_coins} coins & +${res.reward_xp} XP!`);
      load(); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim");
    } finally { setClaiming(null); }
  };

  if (!profile) return null;

  return (
    <AppShell>
      <h1 className="text-4xl font-black leading-none mb-4">Rewards</h1>

      <div className="glass rounded-3xl p-5 mb-5 flex items-center gap-4"
           style={{ background: "linear-gradient(120deg, rgba(255,122,0,0.28), rgba(255,42,122,0.16))" }}
           data-testid="streak-card">
        <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
          <Flame size={32} className="text-[#FF7A00]" />
        </div>
        <div>
          <div className="text-3xl font-black">{profile.streak} days</div>
          <div className="text-sm text-[#A8B2D1]">Daily streak — play once a day to keep it alive!</div>
        </div>
      </div>

      <h2 className="text-lg font-bold mb-3">Daily Quests</h2>
      <div className="space-y-3">
        {quests.map((q, i) => {
          const pct = Math.min(100, Math.round((q.progress / q.goal) * 100));
          const done = q.progress >= q.goal;
          return (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
              className="glass rounded-3xl p-4" data-testid={`quest-${q.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold">{q.label}</div>
                <div className="flex items-center gap-2 text-xs font-bold">
                  <span className="flex items-center gap-1 text-[#FFD600]"><Coins size={13} />+{q.reward_coins}</span>
                  <span className="flex items-center gap-1 text-[#00E5FF]"><Zap size={13} />+{q.reward_xp}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                       style={{ width: `${pct}%`, background: done ? "#00FF66" : "#FF2A7A" }} />
                </div>
                <span className="text-xs text-[#A8B2D1] w-12 text-right">{q.progress}/{q.goal}</span>
              </div>
              <div className="mt-3">
                {q.claimed ? (
                  <div className="flex items-center gap-1.5 text-sm font-bold text-[#00FF66]">
                    <Check size={16} /> Claimed
                  </div>
                ) : (
                  <button
                    data-testid={`claim-${q.id}`}
                    disabled={!done || claiming === q.id}
                    onClick={() => claim(q.id)}
                    className="btn-3d px-5 py-2 text-sm font-bold text-white disabled:opacity-40 flex items-center gap-1.5"
                    style={{ background: done ? "#FF2A7A" : "rgba(255,255,255,0.08)",
                             boxShadow: done ? "0 4px 0 rgba(0,0,0,.35)" : "none" }}>
                    <Gift size={15} /> {done ? "Claim" : "Keep going"}
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </AppShell>
  );
}
